import { useEffect } from 'react'
import { isNative } from '../native/native'
import { clearNativeNotifications } from '../native/nativePush'

// Notifications stay on the lock screen and in the notification centre after the app is opened, until something closes them. Opening the
// app clears them all, and reading a conversation clears its own (which also covers a push that arrived while the app was open).

interface NotificationSource {
  getNotifications: () => Promise<readonly Pick<Notification, 'close' | 'data'>[]>
}

async function source(): Promise<NotificationSource | null> {
  try {
    const reg = await navigator.serviceWorker?.getRegistration?.()
    return reg && typeof reg.getNotifications === 'function' ? reg : null
  } catch {
    return null
  }
}

/** Closes this device's notifications: every one, or those of one conversation. Never throws. Returns how many were closed. */
export async function clearNotifications(channelId?: string, from?: NotificationSource | null): Promise<number> {
  if (from === undefined && isNative()) return clearNativeNotifications(channelId)
  const reg = from === undefined ? await source() : from
  if (!reg) return 0
  try {
    const shown = await reg.getNotifications()
    const matching = channelId === undefined ? shown : shown.filter((n) => (n.data as { channelId?: string } | null)?.channelId === channelId)
    for (const n of matching) n.close()
    return matching.length
  } catch {
    return 0
  }
}

/** While the app is open: clear every notification when it starts and each time it comes back to the front. */
export function useClearNotificationsWhenOpen(): void {
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') void clearNotifications()
    }
    onVisible()
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [])
}
